import {
  TrackingWorksheet,
  TrackingWorksheetPassSource,
  formatTrackingWorksheetTrackState,
} from '@/models/TrackingWorksheet';
import {
  csvEscape,
  sanitizeCsvFilename,
  triggerCsvDownload,
} from '@/utils/PipelineCsvExport';
import { describeLegSpeedRejection } from '@/services/PigRunSpeedRules';
import { isEndedLifecycleState } from '@/services/TrackingWorksheetBuilder';

function isValidDate(date: Date | null): date is Date {
  return date !== null && !Number.isNaN(date.getTime());
}

function formatUtc(date: Date | null): string {
  return isValidDate(date) ? date.toISOString() : '';
}

function formatBrowserLocal(date: Date | null): string {
  return isValidDate(date) ? date.toLocaleString() : '';
}

function formatPassSource(source: TrackingWorksheetPassSource | null): string {
  if (!source) return '';
  return source.kind === 'manual' ? 'Manual' : `AGM ${source.uid}`;
}

type WorksheetCsvCell = string | number | null;

function escapeWorksheetCsvCell(value: WorksheetCsvCell): string {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? csvEscape(String(value)) : '';
  }

  const textValue = value ?? '';
  const formulaSafeValue = /^[\t ]*[=+\-@]/.test(textValue)
    ? `'${textValue}`
    : textValue;
  return csvEscape(formulaSafeValue);
}

/** Builds RFC 4180 CSV from the same normalized worksheet model used by the UI. */
export function buildTrackingWorksheetCsv(
  worksheet: TrackingWorksheet
): string {
  const { summary } = worksheet;
  // Done or Cancelled (EN-1495 review round 2, R2-9).
  const isCompletedRun = isEndedLifecycleState(summary.lifecycleState);
  const distanceFromLaunchLabel =
    worksheet.distanceFromLaunchSource === 'measured'
      ? 'Pipeline Distance (m)'
      : 'Cumulative GPS Distance (m)';
  const distanceFromLaunchSource =
    worksheet.distanceFromLaunchSource === 'measured'
      ? 'Pipeline stationing'
      : 'GPS cumulative Haversine legs';
  const lifecycleContextLines: WorksheetCsvCell[][] = isCompletedRun
    ? [
        ['Pipeline', summary.pipelineName ?? ''],
        ['Receive Time (UTC)', formatUtc(summary.receiveTimeUtc)],
        ['Passage Count', summary.passageCount],
        ['Verified Passage Count', summary.verifiedPassageCount],
      ]
    : [
        ['Live State Updated (UTC)', formatUtc(summary.lastUpdatedUtc)],
        ['Current Speed (m/s)', summary.currentSpeedMetersPerSecond],
        ['Next Marker', summary.nextMarkerName ?? ''],
        ['Next Marker ETA (UTC)', formatUtc(summary.nextMarkerEtaUtc)],
        ['Estimated Completion (UTC)', formatUtc(summary.endEtaUtc)],
      ];
  const lines: string[] = [
    ['Run Name', summary.runName],
    ['Job Number', summary.jobNumber ?? ''],
    ['Lifecycle State', summary.lifecycleState],
    ['Launch Time (UTC)', formatUtc(summary.launchTimeUtc)],
    ...lifecycleContextLines,
    ['Marker Count', summary.markerCount],
    ['Verified Marker Count', summary.verifiedMarkerCount],
    ['Device Count', summary.deviceCount],
    ['Exported At (UTC)', formatUtc(worksheet.builtAtUtc)],
    ['Speed Unit', 'm/s'],
    ['Distance Unit', 'm'],
    ['Cumulative Distance Source', distanceFromLaunchSource],
    ['Local Timezone', 'Browser local time'],
    [],
    [
      'Marker',
      'Description',
      'Track State',
      'Status',
      'Linked AGMs',
      'Pass Source',
      'Pass Time (UTC)',
      'Pass Time (Browser Local)',
      'ETA (UTC)',
      'ETA (Browser Local)',
      'Countdown (seconds)',
      'Leg Speed (m/s)',
      'Leg Distance (m)',
      'Leg Distance Source',
      distanceFromLaunchLabel,
      'Run Average Speed (m/s)',
      'Latitude',
      'Longitude',
      // Appended last so existing column positions do not move (EN-1495 R3-6).
      'Leg Speed Note',
    ],
  ].map((values) =>
    values.map((value) => escapeWorksheetCsvCell(value)).join(',')
  );

  for (const row of worksheet.rows) {
    lines.push(
      [
        row.markerName,
        row.description,
        formatTrackingWorksheetTrackState(row.trackState),
        row.status,
        row.linkedUids.join('; '),
        formatPassSource(row.passSource),
        formatUtc(row.passageTimeUtc),
        formatBrowserLocal(row.passageTimeUtc),
        formatUtc(row.etaUtc),
        formatBrowserLocal(row.etaUtc),
        row.countdownSeconds,
        row.legSpeedMetersPerSecond,
        row.legDistanceMeters,
        row.legDistanceSource ?? '',
        row.distanceFromLaunchMeters,
        row.runAverageSpeedMetersPerSecond,
        row.latitude,
        row.longitude,
        describeLegSpeedRejection(row.legSpeedRejection),
      ]
        .map((value) => escapeWorksheetCsvCell(value))
        .join(',')
    );
  }

  return `${lines.join('\r\n')}\r\n`;
}

export function downloadTrackingWorksheetCsv(
  worksheet: TrackingWorksheet
): void {
  const filename = `${sanitizeCsvFilename(
    worksheet.summary.runName
  )}-tracking-worksheet.csv`;
  triggerCsvDownload(buildTrackingWorksheetCsv(worksheet), filename);
}
